import {assertUnreachable} from '@wandb/weave/common/util/types';
import * as QueryString from 'query-string';
import {useEffect} from 'react';

import {modifyQueryString} from '../../util/urls';
import {usePropsSelector} from '../hooks';
import {getWorkspaceType} from './names';
import {
  makeWorkspaceFnwIdSelector,
  makeWorkspaceViewSelector,
} from './selectors';
import {
  getNamedWorkspaceType,
  NamedWorkspaceId,
  NW_PUBLISHED_USERNAME,
  WorkspaceTypes,
} from './types';

/** This is the data that we parse out of a workspace URL */
export type WorkspaceUrl =
  // User and default are legacy URLs that should no longer be created.
  // After named workspaces is fully rolled out, we should treat these as legacy
  // values that will result in a migration.
  | {type: 'user'; user: string}
  | {type: 'default'}
  // empty is deprecated as of now - it's bad to have a GET url perform an action,
  // and the code now should not be generating any of these URLs
  | {type: 'empty'}
  // when user lands on the base project URL
  | {type: 'none'}
  // the normal URL for a named workspace
  | {
      type: 'named';
      id: string;
      username: string | undefined;
    };

// These are all legacy and not used in named workspaces
const WorkspaceParamValues = {
  /** @deprecated */
  empty: 'empty',
  default: 'default',
  userPrefix: 'user-',
} as const;

const ParamNames = {
  workspace: 'workspace',
  nwId: 'nw',
  username: 'user',
  published: 'published',
} as const;

/**
 * When the user navigates to a workspaces page, this is the function responsible
 * for parsing the query parameters.
 */
export function readWorkspaceUrl(searchParams: URL['search']): WorkspaceUrl {
  const parsed = QueryString.parse(searchParams);

  const workspaceParam = parsed[ParamNames.workspace];
  const namedParam = parsed[ParamNames.nwId];
  const userParam = parsed[ParamNames.username];
  const publishedParam = ParamNames.published in parsed;
  if (!parsed) {
    return {type: 'none'};
  } else if (workspaceParam != null) {
    const paramValue = firstVal(workspaceParam);
    if (paramValue === WorkspaceParamValues.empty) {
      // This will load an empty workspace. Useful for getting out of broken states
      return {
        type: 'empty',
      };
    } else if (paramValue === WorkspaceParamValues.default) {
      return {
        type: 'default',
      };
    } else if (paramValue?.startsWith(WorkspaceParamValues.userPrefix)) {
      return {
        type: 'user',
        user: paramValue?.slice(WorkspaceParamValues.userPrefix.length),
      };
    }
  } else if (namedParam != null) {
    if (publishedParam && userParam != null) {
      throw new Error('Invalid named workspaces URL');
    }
    let username = userParam != null ? firstVal(userParam) : undefined;
    if (publishedParam) {
      username = NW_PUBLISHED_USERNAME;
    }
    return {
      type: 'named',
      id: firstVal(namedParam),
      username,
    };
  }
  return {
    type: 'none',
  };
}

const firstVal = (paramVal: string | string[]): string => {
  return Array.isArray(paramVal) ? paramVal[0] : paramVal;
};

/**
 * Responsible for updating the browser's URL bar to accurately reflect
 * the workspace that the user is currently viewing.
 */
export function useUpdateViewUrlEffect(workspaceStateId: string) {
  const workspaceView = usePropsSelector(
    makeWorkspaceViewSelector,
    workspaceStateId
  );
  const workspaceFnwId = usePropsSelector(
    makeWorkspaceFnwIdSelector,
    workspaceStateId
  );
  useEffect(() => {
    if (!workspaceView.loading) {
      const {view} = workspaceView;
      const type = getWorkspaceType(view.name);
      switch (type) {
        case WorkspaceTypes.default:
          modifyQueryString(current => ({
            ...current,
            [ParamNames.workspace]: WorkspaceParamValues.default,
          }));
          break;
        case WorkspaceTypes.user:
          modifyQueryString(current => ({
            ...current,
            [ParamNames.workspace]:
              WorkspaceParamValues.userPrefix + view.user.username,
          }));
          break;
        case WorkspaceTypes.named:
          updateUrlForNamedWorkspace(workspaceFnwId, modifyQueryString);
          break;
        default:
          assertUnreachable(type);
      }
    }
  }, [
    workspaceView,
    workspaceView.view,
    workspaceView.view?.id,
    workspaceView.loading,
    workspaceFnwId,
  ]);
}

/**
 * Responsible for updating the browser bar based on what named workspace
 * the user is currently viewing
 * @param modify We inject this param to make this code more testable
 */
export const updateUrlForNamedWorkspace = (
  nwId: NamedWorkspaceId | undefined,
  modify: typeof modifyQueryString
) => {
  // if we don't have an id yet, we don't want to add the named workspace URL params yet
  let nwParams: Record<string, any>;
  if (nwId == null) {
    nwParams = {};
  } else {
    nwParams = getUrlParams(nwId);
  }

  modify(current => {
    // We always remove the workspace param if it's present
    const {
      [ParamNames.workspace]: discard1, // we don't want any of these params, but can't use _ more than once
      [ParamNames.published]: discard2,
      [ParamNames.username]: discard3,
      [ParamNames.nwId]: discard4,
      ...rest
    } = current;
    return {
      ...rest,
      ...nwParams,
    };
  });
};

/**
 * calculate the correct URL string
 */
function getUrlParams(workspaceNwId: NamedWorkspaceId) {
  const templateOrDraftParam =
    getNamedWorkspaceType(workspaceNwId) === 'published'
      ? {[ParamNames.published]: null}
      : {
          [ParamNames.username]: workspaceNwId.username,
        };

  return {
    [ParamNames.nwId]: workspaceNwId.id,
    ...templateOrDraftParam,
  };
}
